"use client";

import { useCallback, useEffect } from "react";
import { globalKeyboardShortcutsManager } from "./keyboard-shortcuts-manager";

/**
 * React hook for registering a global keyboard shortcut with an optional layer.
 *
 * @param key The key to listen for (case-insensitive).
 * @param callback The function to call when the key is pressed.
 * @param disabled If true, the shortcut is disabled.
 * @param layer The layer (priority) of the shortcut. Higher layers take precedence. Defaults to 0.
 */
export function useKeyboardShortcut(
	key: string,
	callback: (e: KeyboardEvent) => void,
	disabled = false,
	layer = 0,
) {
	// Memoize the callback to avoid unnecessary re-registrations.
	const memoizedCallback = useCallback(callback, [callback]);

	useEffect(() => {
		const id = globalKeyboardShortcutsManager.registerShortcut(
			key,
			memoizedCallback,
			disabled,
			layer,
		);
		return () => {
			globalKeyboardShortcutsManager.unregisterShortcut(id);
		};
	}, [key, memoizedCallback, disabled, layer]);
}
